import classNames from 'classnames';
import React from 'react';
import {useEffect, useState, useRef} from 'react';
import {CSSProperty, DROPDOWN_UNITS, SizeUnit, extractUnitAndValue} from 'rendition-core';
import {useViewContext} from 'Contexts/ViewContext';

const UNIT_OPTIONS = DROPDOWN_UNITS.units.map((unit) => unit.value).filter((unit) => unit !== 'auto' && unit !== 'vh');

// Custom hook for showing mixed values
export const useShowMixedValues = (elementRef: React.RefObject<HTMLInputElement>, property: CSSProperty) => {
    const [showMixed, setShowMixed] = useState(false);
    const {selectedNode} = useViewContext();
    useEffect(() => {
        if (elementRef.current) {
            // This is a hack to convert the value to the correct unit
            // it uses the node to get the value so we'll just assign it here and delete it after
            if (selectedNode && selectedNode.styles[property] !== 'Mixed') {
                const result = extractUnitAndValue(selectedNode.styles[property]);
                setShowMixed(false);
                elementRef.current.value = `${result.value}`;
            } else {
                setShowMixed(true);
                elementRef.current.value = '';
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode]);

    return showMixed;
};

interface Props {
    label?: string;
    unit: SizeUnit | 'Mixed';
    onUnitChange?: (unit: SizeUnit) => void;
    onValueChange: (value: number) => void;
    value: number | 'Mixed' | undefined;
    disabled?: boolean;
    dropdown?: boolean;
    inputType: 'underline' | 'rounded';
}

export const NumericTextField: React.FC<Props> = ({
    label,
    unit = 'px',
    onUnitChange,
    onValueChange,
    disabled,
    value,
    dropdown = true,
    inputType = 'underline',
}) => {
    const elementRef = useRef<HTMLInputElement>(null);

    const [localValue, setLocalValue] = useState<number | 'Mixed' | undefined>(value);

    //const debouncedHandlePropertyChange = useDebouncedCallback(onValueChange, 3000);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    switch (inputType) {
        case 'underline':
            return (
                <UnderlineInput
                    ref={elementRef}
                    dropdown={dropdown}
                    options={UNIT_OPTIONS}
                    dropdownDesc={`${label} units`}
                    inputProps={{
                        className: 'placeholder-gray-300',
                        disabled: disabled,
                        value: localValue === 'Mixed' ? undefined : localValue,
                        onChange: (e) => setLocalValue(Number(e.target.value)),
                        onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                if (typeof localValue === 'number') {
                                    onValueChange(localValue);
                                }
                            }
                        },
                        onBlur: () => {
                            if (typeof localValue === 'number') {
                                onValueChange(localValue);
                            }
                        },
                        type: 'number',
                        style: {appearance: 'textfield'},
                        placeholder: localValue === 'Mixed' ? 'Mixed' : '',
                        onWheel: (e: any) => {
                            e.target.blur();
                        },
                    }}
                    disableUnitChange={onUnitChange === undefined}
                    selectProps={{
                        id: `${label}-units`,
                        onChange: (e) => onUnitChange && onUnitChange(e.target.value as SizeUnit),
                        value: unit,
                    }}
                />
            );
        case 'rounded':
            return (
                <RoundedInput
                    ref={elementRef}
                    dropdown={dropdown}
                    options={UNIT_OPTIONS}
                    dropdownDesc={`${label} units`}
                    inputProps={{
                        placeholder: 'Mixed',
                        className: 'placeholder-gray-300',
                        disabled: disabled,
                        value: localValue === 'Mixed' ? '' : localValue,
                        onChange: (e) => setLocalValue(Number(e.target.value)),

                        onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                if (typeof localValue === 'number') {
                                    onValueChange(localValue);
                                }
                            }
                        },
                        onBlur: () => {
                            console.log('hello');
                            if (typeof localValue === 'number') {
                                onValueChange(localValue);
                            }
                        },
                    }}
                    disableUnitChange={onUnitChange === undefined}
                    selectProps={{
                        id: `${label}-units`,
                        onChange: (e) => onUnitChange && onUnitChange(e.target.value as SizeUnit),
                        value: unit,
                    }}
                />
            );
    }
};

interface TextInputProps {
    id?: string;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    dropdown?: boolean;
    options?: string[];
    dropdownDesc?: string;
    selectProps?: React.SelectHTMLAttributes<HTMLSelectElement>;
    disableUnitChange?: boolean;
}

const UnderlineInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    ({id, inputProps, dropdownDesc, dropdown = false, options = [], selectProps, disableUnitChange}, ref) => {
        return (
            <div className={`inline-flex ${inputProps?.disabled && 'opacity-30'}`}>
                <input
                    {...inputProps}
                    id={id}
                    ref={ref}
                    type="number"
                    maxLength={5}
                    className={classNames(
                        'w-full border-b-2 bg-white bg-opacity-0 py-0.5',
                        'text-left text-xs font-medium text-white outline-none transition-colors placeholder:text-gray-400 placeholder:opacity-20 focus:border-blue-500',
                        inputProps?.className
                    )}
                />
                {dropdown && (
                    <div className="flex items-center">
                        {dropdownDesc && (
                            <label htmlFor={selectProps?.id} className="sr-only">
                                {dropdownDesc}
                            </label>
                        )}
                        <select
                            {...selectProps}
                            disabled={disableUnitChange === true}
                            className={classNames(
                                'h-full appearance-none bg-transparent py-0 pl-1 text-[10px] font-medium text-white',
                                selectProps?.className,
                                disableUnitChange ? 'cursor-auto' : 'cursor-pointer'
                            )}
                        >
                            {options.map((option) => (
                                <option key={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    }
);

const RoundedInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    ({id, inputProps, dropdownDesc, dropdown = false, options = [], selectProps, disableUnitChange}, ref) => {
        const hasBeenFocusedRef = useRef(false);

        const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
            if (!hasBeenFocusedRef.current) {
                e.currentTarget.select();
                hasBeenFocusedRef.current = true;
            }
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
            hasBeenFocusedRef.current = false;
            // Flush the debounce mechanism.
            if (inputProps?.onBlur !== undefined) {
                inputProps.onBlur(e);
            }
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (!hasBeenFocusedRef.current) {
                e.target.select();
                hasBeenFocusedRef.current = true;
            }
        };

        return (
            <div className={`inline-flex ${inputProps?.disabled && 'opacity-30'}`}>
                <input
                    {...inputProps}
                    id={id}
                    ref={ref}
                    maxLength={5}
                    type="number"
                    className={classNames(
                        'h-8 w-full rounded-md border-2 border-transparent bg-neutral-950 px-1 focus:border-blue-500',
                        'text-left text-xs font-medium text-white outline-none transition-colors placeholder:text-gray-400 placeholder:opacity-20 ',
                        inputProps?.className
                    )}
                    onClick={handleClick}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                />
                {dropdown && (
                    <div className="flex items-center">
                        {dropdownDesc && (
                            <label htmlFor={selectProps?.id} className="sr-only">
                                {dropdownDesc}
                            </label>
                        )}
                        <select
                            {...selectProps}
                            disabled={disableUnitChange === true}
                            className={classNames(
                                'h-full appearance-none bg-transparent py-0 pl-1 text-[10px] font-medium text-white',
                                selectProps?.className,
                                disableUnitChange ? 'cursor-auto' : 'cursor-pointer'
                            )}
                        >
                            {options.map((option) => (
                                <option key={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    }
);
